import React, { useEffect, useState } from "react";
import ProfileDetails from "./ProfileDetails";
import ProfessionDetails from "./ProfessionDetails";
import ReligionDetails from "./ReligionDetails";
import MentorDetails from "./MentorDetails";
import ExpectationDetails from "./ExpectationDetails";
import PhotoDetails from "./PhotoDetails";
import Images from "../../utils/images";
import Matches from "./Matches";
import { authorizedGet, authorizedDel } from "../../Config/networkWithToken";
import configuration from "../../Config/configuration";
import MessageBar, {
  config,
  DOBASEURL,
  errorNotify,
  getValueFromLocalStorage,
  localStorageKey,
  successNotify,
} from "../../utils/helper";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import Notes from "./Notes";
import ViewQuestionnaire from "../../Components/ViewQuestionnarie/ViewQuestionnaire";
import Settings from "../../Components/AdminSettings/AdminSettings";
import Spinner from "../../Components/Spinner/Spinner";
import Strings from "../../utils/Strings";
import { IoSettingsOutline } from "react-icons/io5";
import { FaEdit, FaHeart, FaRegHeart } from "react-icons/fa";
import { useSearchParams } from "react-router-dom";
import RequestAndSuggestions from "./RequestAndSuggestion";
import { MdPersonOff } from "react-icons/md";
import ViewQuestionnaireAnswers from "../../Components/ViewQuestionnaireAnswers/ViewQuestionnaireAnswers";
import ProfileLog from "./ProfileLog";
import UserActivityLog from "./UserActivityLog";
import { useFavourites, useToggleFavourite, getProfileId } from "../../Admin/Pages/Favourites/useFavourites";

const ViewProfile = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = parseInt(searchParams.get("tab")) || 0;
  const [activeTab, setActiveTab] = useState(0);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const localStorageUserData = JSON.parse(localStorage.getItem("userData"));
  const userRole = localStorageUserData?.role;
  const { userId, requestedUserId, pageNo } = useParams();
  const [approvedQuestionaire, setApprovedQuestionaire] = useState([]);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  // Coordinators get the same view as admin/superAdmin (tabs, details) but are
  // fully read-only -- mutating triggers (edit, settings, favourite) gate on canManage instead.
  const isAdminLike = userRole === "admin" || userRole === "superAdmin" || userRole === "coordinator";
  const canManage = userRole === "admin" || userRole === "superAdmin";
  const [appliedFilterData, setAppliedFilterData] = useState([]);
  const [favouriteLoading, setFavouriteLoading] = useState(false);

  // Same favourite toggle as the profile card — shares the react-query cache
  // via the logged-in admin's id, so the heart icon here stays in sync with it.
  // Admin accounts store their own id under "adminId", not "_id".
  const adminId = localStorageUserData?.adminId;
  const profileId = getProfileId(userData) || userId;
  const { data: favourites = [] } = useFavourites(adminId, {
    enabled: canManage,
  });
  const isFavourite = favourites.some((item) => getProfileId(item) === profileId);
  const toggleFavourite = useToggleFavourite(adminId);

  const handleToggleFavourite = async () => {
    if (!profileId || favouriteLoading) return;
    setFavouriteLoading(true);
    try {
      await toggleFavourite.mutateAsync({ profileId, isFavourite });
    } catch (error) {
      console.log(error);
      errorNotify("Could not update favourite. Please try again.");
    } finally {
      setFavouriteLoading(false);
    }
  };

  useEffect(() => {
    if (userData) {
      setActiveTab(initialTab);
    }
  }, [userData]);

  const fetchSingleUser = async () => {
    try {
      setLoading(true);

      const queryParams = requestedUserId
        ? `userId=${userId}&requestUserId=${requestedUserId}`
        : `userId=${userId}`;

      const res = await authorizedGet(
        `${configuration.apis.singleUser}?${queryParams}`,
        config,
      );

      const response = res?.data?.data;

      if (response) {
        setUserData(response);
      }
    } catch (err) {
      let errorMessage =
        "An unexpected error occurred. Please try again later.";

      if (err.response) {
        errorMessage =
          err.response.data.message || "Server error. Please try again.";
      } else if (err.request) {
        errorMessage = "Network error";
      }

      errorNotify(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getApproveRequests = async () => {
    setLoading(true);
    try {
      const res = await authorizedGet(
        `${configuration.apis.getQuestions}?userId=${userId}&requestedUserId=${localStorageUserData?._id}`,
        config,
      );

      const response = res?.data?.data;
      if (res?.data?.status) {
        setApprovedQuestionaire(response);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSingleUser();
    getApproveRequests();
  }, [userId, requestedUserId]);

  const tabList = [
    "Profile",
    "Profession",
    "Religion & Family",
    "Mentor",
    "Photo",
    "Expectation",
    "Questionnaire",
    ...(isAdminLike ? ["Notes"] : []),
    ...(isAdminLike ? ["ProfileLog"] : []),
    ...(isAdminLike &&
    !(
      userData?.isBlocked ||
      userData?.maritalStatus === "married" ||
      userData?.profileCompletionPercentage < 80
    )
      ? ["Matches"]
      : []),
    ...(isAdminLike && userData?.profileCompletionPercentage >= 80
      ? ["Requests"]
      : []),
    ...(isAdminLike && userData?.profileCompletionPercentage >= 80
      ? ["ActivityLog"]
      : []),
  ];

  const handleTabClick = (index) => {
    setActiveTab(index);
    setSearchParams({ tab: index });
  };

  const navigate = useNavigate();
  const location = useLocation();
  const pathName = location.pathname;
  const getPath = pathName.split("/");

  const handleGoBack = () => {
    navigate(-1);
  };

  const refreshUserData = () => {
    fetchSingleUser();
  };
  return (
    <div
      className={`font-jost flex min-h-screen w-full justify-center bg-bgColor ${isAdminLike ? "mt-8" : ""}`}
    >
      <div className="flex w-full flex-col" style={{ maxWidth: "1400px" }}>
        {(isAdminLike ||
          getPath[1] === "interest" ||
          getPath[1] === "suggestion" ||
          getPath[1] === "accepted-users") && (
          <div className="relative flex w-full items-center p-2 md:p-4">
            {/* Left section */}
            <div className="flex items-center gap-2 md:gap-4">
              <div
                className="font-jost flex w-[100px] cursor-pointer items-center gap-1 overflow-hidden rounded-full bg-textYellowLight
                           pr-1 sm:w-[130px] md:w-[180px] md:gap-0"
              >
                <div className="w-1/3 overflow-hidden rounded-full">
                  <img
                    className="h-10 w-10 rounded-full object-cover"
                    src={
                      userData?.profile?.photos?.images?.[0]
                        ? DOBASEURL + userData?.profile?.photos?.images?.[0]
                        : userData?.gender === "male"
                          ? Images.MEN
                          : Images.WOMEN
                    }
                    alt=""
                    onContextMenu={(e) => e.preventDefault()}
                    onDragStart={(e) => e.preventDefault()}
                  />
                </div>
                <p className="scrollbar-hide relative w-2/3 overflow-x-auto whitespace-nowrap text-center text-sm md:text-base">
                  {userData?.name}
                </p>
              </div>
              {(((userData?.isActive ||
                (!userData?.hasOwnProperty("isActive") &&
                  userRole === "admin" &&
                  userData?.profileCompleted)) &&
                !userData?.isRequested &&
                !userData?.isMatched) ||
                (userData?.profileCompletionPercentage > 0 &&
                  (!userData?.profileCompleted || !userData?.isActive) &&
                  userRole === "admin" &&
                  !userData?.isRequested &&
                  !userData?.isMatched)) && (
                <MdPersonOff className="flex-shrink-0 text-2xl text-red-600 md:text-3xl" />
              )}
            </div>

            <h1
              className="sm:text-md absolute left-1/2 top-1/2 mb-2 -translate-x-1/2
               -translate-y-1/2 transform whitespace-nowrap px-2 text-sm text-textGreen sm:mb-0 md:text-3xl"
            >
              Profile Details
            </h1>

            {/* Right side */}
            <div className="ml-auto flex items-center gap-3">
              {canManage && (
                <button
                  type="button"
                  onClick={handleToggleFavourite}
                  disabled={favouriteLoading}
                  aria-label={isFavourite ? "Remove from favourites" : "Add to favourites"}
                  title={isFavourite ? "Remove from favourites" : "Add to favourites"}
                  className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-textYellowLight shadow-sm transition-transform hover:scale-110 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isFavourite ? (
                    <FaHeart className="text-lg text-red-500" />
                  ) : (
                    <FaRegHeart className="text-lg text-gray-500" />
                  )}
                </button>
              )}
              <p
                onClick={handleGoBack}
                className="cursor-pointer whitespace-nowrap text-sm text-blue-600 underline md:text-base"
              >
                Back
              </p>
            </div>
          </div>
        )}

        <div className="flex w-full flex-col px-4">
          <div className="h-auto w-full flex-grow overflow-auto rounded-lg bg-white p-4 shadow-md">
            <div className="flex gap-2  text-textGreen">
              <ul className="flex w-full flex-wrap  border-b-[0.5px]">
                {tabList.map((list, index) => (
                  <li
                    key={index}
                    className={`playFair cursor-pointer p-3 text-center transition-colors duration-300 
                      ${loading ? "cursor-not-allowed" : ""}
                      ${activeTab === index ? "playFair border-b-2 border-textYellow text-textYellow after:bg-textYellow" : "text-textGreen"}`}
                    onClick={() => !loading && handleTabClick(index)}
                  >
                    {list}
                  </li>
                ))}
              </ul>
              {canManage && (
                <FaEdit
                  className="mr-2 cursor-pointer"
                  size={30}
                  onClick={() => setShowEditModal(true)}
                />
              )}
              {canManage && (
                <IoSettingsOutline
                  className="mr-4 cursor-pointer"
                  size={30}
                  onClick={() => setShowSettingsModal(true)}
                />
              )}
            </div>

            {activeTab === 0 && (
              <ProfileDetails userData={userData} Loading={loading} />
            )}
            {activeTab === 1 && (
              <ProfessionDetails userData={userData} Loading={loading} />
            )}
            {activeTab === 2 && (
              <ReligionDetails userData={userData} Loading={loading} />
            )}
            {activeTab === 3 && (
              <MentorDetails userData={userData} Loading={loading} />
            )}
            {activeTab === 4 && (
              <PhotoDetails userData={userData} Loading={loading} />
            )}
            {activeTab === 5 && (
              <ExpectationDetails userData={userData} Loading={loading} />
            )}
            {activeTab === 6 && (
              <>
                {userData?.profile?.questionnaire?.isQuestionnaireForm ? (
                  <ViewQuestionnaireAnswers
                    userId={userId}
                    requestedUserId={localStorageUserData?._id}
                    questionnaireData={approvedQuestionaire}
                    userData={userData}
                  />
                ) : (
                  <ViewQuestionnaire
                    userId={userId}
                    requestedUserId={localStorageUserData?._id}
                    questionnaireData={approvedQuestionaire}
                    userData={userData}
                  />
                )}
              </>
            )}
            {/* {userRole === "admin" && activeTab === 7 && (
              <Matches gender={userData?.gender} />
            )} */}

            {isAdminLike && activeTab === tabList.indexOf("Notes") && <Notes />}
            {isAdminLike && activeTab === tabList.indexOf("ProfileLog") && <ProfileLog userId={userId} />}
            {isAdminLike &&
              activeTab === tabList.indexOf("Matches") &&
              !(
                userData?.isBlocked ||
                userData?.maritalStatus === "married" ||
                userData?.profileCompletionPercentage < 80
              ) && (
                <Matches
                  appliedFilterData={appliedFilterData}
                  setAppliedFilterData={setAppliedFilterData}
                  gender={userData?.gender}
                />
              )}
            {isAdminLike &&
              activeTab === tabList.indexOf("Requests") &&
              userData?.profileCompletionPercentage >= 80 && (
                <RequestAndSuggestions />
              )}
            {isAdminLike &&
              activeTab === tabList.indexOf("ActivityLog") &&
              userData?.profileCompletionPercentage >= 80 && (
                <UserActivityLog userId={userId} />
              )}
          </div>
        </div>

        {/* Settings Modal */}
        {showSettingsModal && (
          <Settings
            userData={userData}
            userId={userId}
            onClose={() => setShowSettingsModal(false)}
            onStatusChange={refreshUserData}
          />
        )}
        {/* Settings Modal */}
        {showEditModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="rounded-lg bg-white p-6 shadow-lg">
              <p className="mb-4 text-center">{Strings.EDIT_USER}</p>
              <div className="flex justify-around">
                <Link to="/editprofile" state={{ userId: userId }}>
                  <button className="rounded bg-blue-500 px-4 py-2 text-white">
                    Yes
                  </button>
                </Link>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="rounded bg-gray-300 px-4 py-2"
                >
                  No
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <MessageBar />
    </div>
  );
};

export default ViewProfile;
